import { message } from "antd";
import JSZip from "jszip";

/**
 * Processes the uploaded folder, organizing files into child folders
 * and creating ZIPs for each subfolder.
 * @param {FileList} fileList - The list of uploaded files.
 * @returns {Promise<Array<{ name: string, zipFile: Blob }>>} - An array of ZIP files with folder names.
 */
export const processFolderUpload = async (fileList, cameraIds) => {
  const files = Array.from(fileList);
  const folderStructure = {};

  // Organizing files into their respective child folders
  files.forEach((file) => {
    const pathParts = file.webkitRelativePath.split("/");
    if (pathParts.length > 1) {
      const folderName = pathParts[1]; // Extract child folder name
      if (!folderStructure[folderName]) {
        folderStructure[folderName] = [];
      }
      folderStructure[folderName].push(file);
    }
  });

  const folderNames = Object.keys(folderStructure);
  const cameraNames = cameraIds.map((camera) => camera.name);

  // Find unmatched folder names
  const unmatchedFolders = folderNames.filter(folderName => !cameraNames.includes(folderName));

  if (unmatchedFolders.length > 0) {
    // message.warning(`The following folders do not match any camera IDs: ${unmatchedFolders.join(", ")}. Please check and try again.`);
    throw new Error(`Folder names do not match with camera IDs: ${unmatchedFolders.join(", ")}`);
  }

  // Create ZIPs for each child folder
  const zipPromises = Object.entries(folderStructure).map(
    async ([folderName, files]) => {
      const zip = new JSZip();
      files.forEach((file) => zip.file(file.name, file)); // Add files to ZIP

      const zipBlob = await zip.generateAsync({ type: "blob" });
      return { name: `${folderName}.zip`, zipFile: zipBlob };
    }
  );

  return Promise.all(zipPromises);
};


/**
 * Uploads ZIP files to the given pre-signed URLs.
 * @param {Array} zipFiles - Array of ZIP files (Blobs).
 * @param {Array} uploadUrls - Array of pre-signed URLs.
 * @returns {Promise<Array>} - Array of upload results.
 */
export const uploadZipFiles = async (zipFiles, uploadUrls) => {
  if (zipFiles.length !== uploadUrls.length) {
    console.error("Mismatch: Number of ZIP files and URLs do not match.");
    return [];
  }

  const uploadPromises = zipFiles.map((zipFile, index) => {
    const url = uploadUrls[index];
    return fetch(url, {
      method: "PUT",
      body: zipFile.zipFile, // Binary data (Blob)
      headers: {
        "Content-Type": "application/zip", // Ensures correct MIME type
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to upload ${zipFile.name}: ${response.statusText}`);
        }
        return { name: zipFile.name, status: "Uploaded", url };
      })
      .catch((error) => {
        console.error(error);
        return { name: zipFile.name, status: "Failed", error };
      });
  });

  return Promise.all(uploadPromises);
};
